import * as React from "react";
// eslint-disable-next-line @1js/office-online-rules/no-v8-imports-for-fluentui -- disabling for existing usage
import type { ITheme } from "@fluentui/react";
import type { AppStrings } from "@1js/acui-common";
import type { ControlInMenuRendererFunction } from "@1js/acui-menu";
import { AppThemeConsumer } from "@1js/acui-theme";
import type { RibbonMode } from "../Ribbon/Ribbon.Props";
import type { RibbonModeToggleProps } from "../RibbonBottomBar";
import { ribbonModeToggleId } from "../RibbonBottomBar";
import { isReducedAnimations } from "@1js/window-util";
import {
  getModeChevronStyles,
  getRibbonModeChevronClassNames,
} from "./RibbonModeChevron.styles";
import { deriveTabKeytip, getTopLevelKeytipProps } from "@1js/acui-keytip";
import { RibbonButton, RibbonFlyoutAnchor } from "../../Controls";

export interface OptionalModeChevronProps {
  preventMenuExecution?: boolean;
  onModeChevronFocus?: () => void;
  disableAnimations?: boolean;
  customStyles?: string;
  renderAsFlyout?: boolean;
  shyRibbonIsEnabled?: boolean;
  ribbonDisplayDialogEnabled?: boolean;
}

export const renderModeChevron = (
  mode: RibbonMode | undefined,
  ribbonModeToggleProps: RibbonModeToggleProps,
  ribbonModeLabel: string,
  ribbonBarHeight: number,
  appStrings: AppStrings,
  controlInMenuRendererFunction: ControlInMenuRendererFunction,
  optionalModeChevronProps?: OptionalModeChevronProps
): JSX.Element => {
  const reducedAnimations =
    isReducedAnimations() || optionalModeChevronProps?.disableAnimations;

  const { onRibbonModeToggleHoverFocus } = ribbonModeToggleProps;
  const onFocusHandler = () => {
    optionalModeChevronProps?.onModeChevronFocus?.();
    onRibbonModeToggleHoverFocus && onRibbonModeToggleHoverFocus();
  };

  return (
    <AppThemeConsumer>
      {(theme: ITheme) => {
        const classNames = getRibbonModeChevronClassNames(theme, {
          ribbonBarHeight,
          customStyles: optionalModeChevronProps?.customStyles,
        });
        return (
          <div
            className={classNames.chevronContainer}
            onMouseEnter={onRibbonModeToggleHoverFocus}
            onFocus={onFocusHandler}
            onTouchStart={onRibbonModeToggleHoverFocus}
          >
            {buildRibbonModeChevronButton(
              ribbonModeToggleProps,
              mode,
              !!reducedAnimations,
              ribbonModeLabel,
              appStrings,
              controlInMenuRendererFunction,
              optionalModeChevronProps
            )}
          </div>
        );
      }}
    </AppThemeConsumer>
  );
};

const buildRibbonModeChevronButton = (
  ribbonModeToggleProps: RibbonModeToggleProps,
  mode: RibbonMode | undefined,
  reducedAnimations: boolean,
  ribbonModeLabel: string,
  appStrings: AppStrings,
  controlInMenuRendererFunction: ControlInMenuRendererFunction,
  optionalModeChevronProps?: OptionalModeChevronProps
): JSX.Element => {
  const {
    onRibbonModeToggle,
    ribbonModeButtonProps,
    isRibbonModeToggleDisabled,
    ribbonModeToggleIcon,
  } = ribbonModeToggleProps;

  const keytipProps = appStrings.ribbonModeChevronKeytip
    ? deriveTabKeytip(
        getTopLevelKeytipProps(appStrings.ribbonModeChevronKeytip),
        onRibbonModeToggle
      )
    : undefined;

  if (optionalModeChevronProps?.renderAsFlyout) {
    return (
      <AppThemeConsumer>
        {(theme: ITheme) => {
          return (
            <RibbonFlyoutAnchor
              type="AppFlyoutAnchorProps"
              id={ribbonModeToggleId}
              label={appStrings.ribbonModeChevronLabel}
              variant="ribbonModeButton"
              styles={getModeChevronStyles(
                mode === "Multiline",
                theme,
                reducedAnimations,
                optionalModeChevronProps?.shyRibbonIsEnabled
              )}
              keytipProps={keytipProps}
              controlInMenuRendererFunction={controlInMenuRendererFunction}
              preventMenuExecution={
                optionalModeChevronProps?.preventMenuExecution
              }
              {...ribbonModeButtonProps}
              showTooltip={true}
            />
          );
        }}
      </AppThemeConsumer>
    );
  }
  return (
    <AppThemeConsumer>
      {(theme: ITheme) => {
        return (
          <RibbonButton
            type="AppButtonProps"
            label={ribbonModeLabel}
            id={ribbonModeToggleId}
            onExecute={onRibbonModeToggle}
            icon={ribbonModeToggleIcon}
            hideLabel={true}
            variant={"ribbonModeButton"}
            variantId={`ribbonModeButton${mode}`}
            styles={getModeChevronStyles(
              mode === "Multiline",
              theme,
              reducedAnimations,
              optionalModeChevronProps?.shyRibbonIsEnabled
            )}
            disabled={isRibbonModeToggleDisabled}
            keytipProps={keytipProps}
            ariaExpanded={mode === "Multiline"}
            showTooltip={true}
          />
        );
      }}
    </AppThemeConsumer>
  );
};
